import template from './refinanceAnalysis.template.html';

export default {
  template,
  bindings: {
    formName: '<',
    refinanceAnalysis: '<',
    products: '<',
    fundingWorksheets: '<',
    lenders: '<',
    otherMortgages: '<',
    isQuickTool: '<',
    isTicketSettled: '<',
    onFinished: '&'
  },
  controller: class controller {
    constructor(
      $rootScope,
      $mdUtil,
      $mdMedia,
      helperService,
      homeLoanService,
      ticketFactFindService,
      TransactionType
    ) {
      'ngInject';

      const ctrl = this;
      ctrl.mdMedia = $mdMedia;
      ctrl.currentOrganization = $rootScope.CurrentOrganization;
      ctrl.currentDateFormat = $rootScope.CurrentDateFormatPrimary();
      ctrl.currentCurrency = $rootScope.CurrentCurrency();
      ctrl.organizationCountry = '';
      ctrl.preferredProduct = null;
      ctrl.areThereSecuritiesWithTransactionTypeOfRefinanceOrEquityRelease = false;
      ctrl.isReady = false;

      ctrl.initialize = () => {
        ctrl.organizationCountry = helperService.getOrganizationCountry().name;
        ctrl.preferredProduct = homeLoanService.getPreferredProduct(ctrl.products);
        ctrl.refinanceAnalysis = ctrl.refinanceAnalysis || {};
        ctrl.fundingWorksheets = ctrl.fundingWorksheets || {};
        ctrl.lenders = ctrl.lenders || [];
        ctrl.otherMortgages = ctrl.otherMortgages || [];
        ctrl.isQuickTool = ctrl.isQuickTool || false;
        ctrl.isTicketSettled = ctrl.isTicketSettled || false;
        if (!ctrl.isTicketSettled) {
          ctrl.areThereSecuritiesWithTransactionTypeOfRefinanceOrEquityRelease();
        } else {
          ctrl.isReady = true;
        }
      };

      ctrl.areThereSecuritiesWithTransactionTypeOfRefinanceOrEquityRelease = () => {
        if (!ctrl.isQuickTool) {
          ctrl.areThereSecuritiesWithTransactionTypeOfRefinanceOrEquityRelease = _.some(
            ctrl.fundingWorksheets.fundsRequired,
            (fR) =>
              fR?.transactionType?.name === TransactionType.refinance.name ||
              fR?.transactionType?.name === TransactionType.equity_release.name
          );
          if (ctrl.areThereSecuritiesWithTransactionTypeOfRefinanceOrEquityRelease) {
            ctrl.setRefinanceAnalysis();
          }
        } else {
          ctrl.setRefinanceAnalysis();
        }
        ctrl.isReady = true;
      };

      ctrl.setRefinanceAnalysis = () => {
        if (ctrl.isQuickTool || (!ctrl.refinanceAnalysis?.isEdit && ctrl.preferredProduct)) {
          let totalProposedLoanAmount = 0;
          let refinancingLoans = 0;
          let mortgageRegistrationFees = 0;
          let firstInterestRate = 0;
          if (!ctrl.isQuickTool) {
            const lender = ctrl.getLender(ctrl.preferredProduct?.idLender);
            if (ctrl.fundingWorksheets.fundsAvailable.length) {
              totalProposedLoanAmount = _.reduce(
                ctrl.fundingWorksheets.fundsAvailable || [],
                (sum, n) => _.add(sum, _.toNumber(n?.proposedLoanAmount || 0)),
                0
              );
            }
            for (let fA = 0; fA < ctrl.fundingWorksheets?.fundsRequired?.length; fA += 1) {
              const fundRequired = ctrl.fundingWorksheets.fundsRequired[fA];
              const mortgage = ctrl.getOtherMortgage(fundRequired.idSecurity);
              if (mortgage) {
                firstInterestRate = mortgage?.interestRate || 0;
              }
              if (
                (fundRequired?.transactionType?.name === TransactionType.refinance.name ||
                  fundRequired?.transactionType?.name ===
                    TransactionType.equity_release.name) &&
                mortgage &&
                lender &&
                mortgage.lender.toLowerCase() !== lender.abbr.toLowerCase()
              ) {
                refinancingLoans += _.toNumber(fundRequired?.existingLoanBalance || 0);
                mortgageRegistrationFees += _.toNumber(
                  fundRequired?.mortgageRegistrationFees || 0
                );
              }
            }
          }
          ctrl.refinanceAnalysis.refinance.refinancingLoans = refinancingLoans;
          ctrl.refinanceAnalysis.refinance.additionalFundsRequested =
            _.subtract(totalProposedLoanAmount, refinancingLoans) > 0
              ? _.subtract(totalProposedLoanAmount, refinancingLoans)
              : 0;
          ctrl.refinanceAnalysis.estimatedEntryFees.totalBank = !ctrl.isQuickTool
            ? ctrl.preferredProduct?.repayments?.upfrontFees || 0
            : 0;
          ctrl.refinanceAnalysis.estimatedEntryFees.mortgageRegistration = mortgageRegistrationFees;
          ctrl.refinanceAnalysis.refinanceBenefit.interestRate.current = firstInterestRate;
          ctrl.refinanceAnalysis.refinanceBenefit.interestRate.proposed = !ctrl.isQuickTool
            ? ctrl.preferredProduct?.productSplits[0]?.interestRate || 0
            : 0;
          ctrl.refinanceAnalysis.refinanceBenefit.loanTermRemaining.proposed = !ctrl.isQuickTool
            ? _.multiply(ctrl.preferredProduct?.productSplits[0]?.loanTerm || 0, 12)
            : 0;
          ctrl.refinanceAnalysis.refinanceBenefit.annualOngoingFees.proposed = !ctrl.isQuickTool
            ? ctrl.preferredProduct?.repayments?.annualFees || 0
            : 0;
          ctrl.refinanceAnalysis.refinanceBenefit.cashback = !ctrl.refinanceAnalysis
            ?.refinanceBenefit?.isCashbackEdited
            ? ctrl.preferredProduct?.costs?.cashback || 0
            : ctrl.refinanceAnalysis?.refinanceBenefit?.cashback || 0;
        }
        ctrl.saveRefinanceAnalysis();
      };

      ctrl.getRefinanceCost = () => {
        ctrl.refinanceAnalysis.refinanceCost.onceOffRefinance = _.sum([
          _.toNumber(ctrl.refinanceAnalysis?.estimatedExitFees?.discharge || 0),
          _.toNumber(ctrl.refinanceAnalysis?.estimatedExitFees?.deferredEstablishment || 0),
          _.toNumber(ctrl.refinanceAnalysis?.estimatedExitFees?.breakCosts || 0),
          _.toNumber(ctrl.refinanceAnalysis?.estimatedExitFees?.otherAssociatedExitCosts || 0),
          _.toNumber(ctrl.refinanceAnalysis?.estimatedEntryFees?.totalBank || 0),
          _.toNumber(ctrl.refinanceAnalysis?.estimatedEntryFees?.lmi || 0),
          _.toNumber(ctrl.refinanceAnalysis?.estimatedEntryFees?.mortgageRegistration || 0),
          _.toNumber(ctrl.refinanceAnalysis?.estimatedEntryFees?.otherAssociatedEntryCosts || 0)
        ]);
      };

      ctrl.getTotalRefinanceFundsRequired = () => {
        if (
          !ctrl.refinanceAnalysis?.isEdit ||
          (ctrl.refinanceAnalysis?.isEdit && ctrl.isQuickTool)
        ) {
          ctrl.refinanceAnalysis.refinanceFunding.totalFundsRequired = _.add(
            ctrl.refinanceAnalysis?.refinance?.refinancingLoans || 0,
            ctrl.refinanceAnalysis?.refinanceCost?.onceOffRefinance || 0
          );
        }
      };

      ctrl.getRequiredLoanAmount = () => {
        if (!ctrl.refinanceAnalysis?.isEdit) {
          let requiredLoanAmount = 0;
          if (ctrl?.fundingWorksheets?.fundsAvailable?.length === 1) {
            requiredLoanAmount =
              ctrl.fundingWorksheets.fundsAvailable[0]?.proposedLoanAmount || 0;
          } else {
            for (let fR = 0; fR < ctrl.fundingWorksheets?.fundsRequired?.length; fR += 1) {
              const fundRequired = ctrl.fundingWorksheets.fundsRequired[fR];
              requiredLoanAmount +=
                fundRequired?.transactionType?.name === TransactionType.refinance.name ||
                fundRequired?.transactionType?.name === TransactionType.equity_release.name
                  ? ctrl.fundingWorksheets?.fundsAvailable[fR]?.proposedLoanAmount || 0
                  : 0;
            }
          }
          ctrl.refinanceAnalysis.refinanceFunding.requiredLoanAmount = requiredLoanAmount;
        }
      };

      ctrl.getSurplusCustomerContribution = () => {
        ctrl.refinanceAnalysis.refinanceFunding.surplusCustomerContribution = _.subtract(
          ctrl.refinanceAnalysis?.refinanceFunding?.requiredLoanAmount || 0,
          ctrl.refinanceAnalysis?.refinanceFunding?.totalFundsRequired || 0
        );
      };

      ctrl.getMonthlyHomeLoanRepaymentCurrent = () => {
        if (
          _.toNumber(ctrl.refinanceAnalysis?.refinanceBenefit?.interestRate?.current || 0) &&
          _.toNumber(
            ctrl.refinanceAnalysis?.refinanceBenefit?.loanTermRemaining?.current || 0
          ) &&
          _.toNumber(ctrl.refinanceAnalysis?.refinance?.refinancingLoans || 0)
        ) {
          ctrl.refinanceAnalysis.refinanceBenefit.monthlyHomeLoanRepayment.current = ticketFactFindService.getPayment(
            _.divide(
              _.divide(
                _.toNumber(
                  ctrl.refinanceAnalysis?.refinanceBenefit?.interestRate?.current || 0
                ),
                100
              ),
              12
            ),
            _.toNumber(
              ctrl.refinanceAnalysis?.refinanceBenefit?.loanTermRemaining?.current || 0
            ),
            _.toNumber(ctrl.refinanceAnalysis?.refinance?.refinancingLoans || 0)
          );
        }
      };

      ctrl.getMonthlyHomeLoanRepaymentProposed = () => {
        if (
          _.toNumber(ctrl.refinanceAnalysis?.refinanceBenefit?.interestRate?.proposed || 0) &&
          _.toNumber(
            ctrl.refinanceAnalysis?.refinanceBenefit?.loanTermRemaining?.proposed || 0
          ) &&
          _.toNumber(ctrl.refinanceAnalysis?.refinance?.refinancingLoans || 0)
        ) {
          ctrl.refinanceAnalysis.refinanceBenefit.monthlyHomeLoanRepayment.proposed = ticketFactFindService.getPayment(
            _.divide(
              _.divide(
                _.toNumber(
                  ctrl.refinanceAnalysis?.refinanceBenefit?.interestRate?.proposed || 0
                ),
                100
              ),
              12
            ),
            _.toNumber(
              ctrl.refinanceAnalysis?.refinanceBenefit?.loanTermRemaining?.proposed || 0
            ),
            _.toNumber(ctrl.refinanceAnalysis?.refinance?.refinancingLoans || 0)
          );
        }
      };

      ctrl.getAnnualBenefit = () => {
        ctrl.refinanceAnalysis.refinanceBenefit.annualBenefit = _.add(
          _.multiply(
            _.subtract(
              _.toNumber(
                ctrl.refinanceAnalysis?.refinanceBenefit?.monthlyHomeLoanRepayment?.current || 0
              ),
              _.toNumber(
                ctrl.refinanceAnalysis?.refinanceBenefit?.monthlyHomeLoanRepayment?.proposed ||
                  0
              )
            ),
            12
          ),
          _.subtract(
            _.toNumber(
              ctrl.refinanceAnalysis?.refinanceBenefit?.annualOngoingFees?.current || 0
            ) -
              _.toNumber(
                ctrl.refinanceAnalysis?.refinanceBenefit?.annualOngoingFees?.proposed || 0
              )
          )
        );
      };

      ctrl.getSavingsOverLoanTerm = () => {
        let monthlyHomeLoanRepaymentAtProposedTerm = 0;
        if (
          _.toNumber(ctrl.refinanceAnalysis?.refinanceBenefit?.interestRate?.current || 0) &&
          _.toNumber(
            ctrl.refinanceAnalysis?.refinanceBenefit?.loanTermRemaining?.proposed || 0
          ) &&
          _.toNumber(ctrl.refinanceAnalysis?.refinance?.refinancingLoans || 0)
        ) {
          monthlyHomeLoanRepaymentAtProposedTerm = ticketFactFindService.getPayment(
            _.divide(
              _.divide(
                _.toNumber(
                  ctrl.refinanceAnalysis?.refinanceBenefit?.interestRate?.current || 0
                ),
                100
              ),
              12
            ),
            _.toNumber(
              ctrl.refinanceAnalysis?.refinanceBenefit?.loanTermRemaining?.proposed || 0
            ),
            _.toNumber(ctrl.refinanceAnalysis?.refinance?.refinancingLoans || 0)
          );
        }
        const loanTermRepaymentCurrent =
          monthlyHomeLoanRepaymentAtProposedTerm *
          _.toNumber(
            ctrl.refinanceAnalysis?.refinanceBenefit?.loanTermRemaining?.proposed || 0
          );
        const loanTermRepaymentProposed =
          _.toNumber(
            ctrl.refinanceAnalysis?.refinanceBenefit?.monthlyHomeLoanRepayment?.proposed || 0
          ) *
          _.toNumber(
            ctrl.refinanceAnalysis?.refinanceBenefit?.loanTermRemaining?.proposed || 0
          );
        ctrl.refinanceAnalysis.refinanceBenefit.savingsOverLoanTerm = _.add(
          _.subtract(loanTermRepaymentCurrent, loanTermRepaymentProposed),
          ctrl.refinanceAnalysis.refinanceBenefit.cashback
        );
      };

      ctrl.getSavingsOverLoanTermWithAdditionalMonthlyRepayment = () => {
        const totalProposedLoanRepayments = _.multiply(
          _.add(
            _.toNumber(
              ctrl.refinanceAnalysis?.refinanceBenefit?.monthlyHomeLoanRepayment?.proposed || 0
            ),
            _.toNumber(
              ctrl.refinanceAnalysis?.refinanceBenefit?.annualOngoingFees?.proposed || 0
            )
          ),
          ctrl.refinanceAnalysis?.refinanceBenefit?.loanTermRemaining?.proposed || 0
        );
        const newTotalRepayment = _.add(
          _.toNumber(
            ctrl.refinanceAnalysis?.refinanceBenefit?.monthlyHomeLoanRepayment?.proposed || 0
          ),
          _.toNumber(ctrl.refinanceAnalysis?.refinanceBenefit?.additionalMonthlyRepayment || 0)
        );
        if (
          ctrl.refinanceAnalysis?.refinanceBenefit?.interestRate?.proposed &&
          newTotalRepayment &&
          ctrl.refinanceAnalysis?.refinance?.refinancingLoans
        ) {
          ctrl.refinanceAnalysis.refinanceBenefit.newCalculatedLoanTerm = ticketFactFindService.getTotalNumberOfPayments(
            _.divide(
              _.divide(
                _.toNumber(
                  ctrl.refinanceAnalysis?.refinanceBenefit?.interestRate?.proposed || 0
                ),
                100
              ),
              12
            ),
            newTotalRepayment,
            _.toNumber(ctrl.refinanceAnalysis?.refinance?.refinancingLoans || 0)
          );
        }
        const totalProposedLoanRepaymentsWithAdditionalPayments = _.multiply(
          _.add(
            newTotalRepayment +
              _.toNumber(
                ctrl.refinanceAnalysis?.refinanceBenefit?.annualOngoingFees?.proposed || 0
              )
          ),
          _.toNumber(ctrl.refinanceAnalysis?.refinanceBenefit?.newCalculatedLoanTerm || 0)
        );
        ctrl.refinanceAnalysis.refinanceBenefit.savingsOverLoanTermWithAdditionalMonthlyRepayment = _.subtract(
          totalProposedLoanRepayments,
          totalProposedLoanRepaymentsWithAdditionalPayments
        );
      };

      ctrl.getLender = (idLender) => _.find(ctrl.lenders || [], (l) => l?.id === idLender);

      ctrl.getOtherMortgage = (idSecurity) =>
        _.find(ctrl.otherMortgages || [], (o) => o?.idSecurity || idSecurity);

      ctrl.saveRefinanceAnalysis = _.debounce(() => {
        ctrl.onFinished({ refinanceAnalysis: ctrl.refinanceAnalysis });
      }, 3000);

      ctrl.$onInit = () => {
        ctrl.initialize();
      };
    }
  }
};
